import { useTranslation } from 'react-i18next';

import type { InterfaceSubServiceStatusType } from '@/types';
import type { FC } from 'react';

import InterfaceSyncTime from '@/components/interface-status/interface-sync-time';
import StatusIndicator from '@/components/interface-status/status-indicator';
import StatusLine from '@/components/shared/status-line';
import useBreakpoint from '@/hooks/use-break-point';
import { cn } from '@/lib/utils';
import calculateTimeDifference from '@/utils/helpers/calculate-time-difference';

const InterfaceSubServiceStatus: FC<InterfaceSubServiceStatusType> = ({
  service,
  hasMultipleSubServices,
  isShowLastSync,
}) => {
  const { t } = useTranslation();
  const isTablet = useBreakpoint('tablet');

  const { serviceName, status, lastSynced, lastSuccessfulSync, nextRunTime, services } =
    service ?? {};

  const { lastSuccessfulSync: lastSuccessfulServicesSync, lastSynced: lastServicesSynced } =
    services?.[0] ?? '';

  const nextSyncTime = calculateTimeDifference(nextRunTime);
  const isShowFailedSync = !!lastSynced && status === 'failed';
  const lastSyncedValue = lastSynced ?? lastServicesSynced;
  const lastSuccessfulSyncedValue = lastSuccessfulSync ?? lastSuccessfulServicesSync;

  return (
    <div key={serviceName} className="flex items-start gap-2 w-full">
      {hasMultipleSubServices && <StatusIndicator status={status} className="size-3 mt-1.5" />}
      <div className="flex justify-between w-full">
        <div className="flex flex-col gap-1 text-subtext w-full">
          <div className="flex justify-between">
            <span
              className={cn('text-sm pb-0.5 pr-4', {
                'font-semibold text-base': hasMultipleSubServices,
              })}
            >
              {serviceName}
            </span>
            {isShowLastSync && (
              <InterfaceSyncTime lastSynced={lastSuccessfulSyncedValue} isShowCheckIcon />
            )}
          </div>
          {isShowFailedSync && (
            <StatusLine
              label={t('interface_status.last_failed_at')}
              className={cn({ 'flex-col': isTablet })}
            >
              <InterfaceSyncTime lastSynced={lastSyncedValue} />
            </StatusLine>
          )}
          {nextSyncTime && (
            <StatusLine label={t('interface_status.next_sync_in')}>
              <span className="font-medium">{nextSyncTime}</span>
            </StatusLine>
          )}
        </div>
      </div>
    </div>
  );
};
export default InterfaceSubServiceStatus;
